(() => {
  const formatMoney = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  };

  class CinotonProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-cinoton-bundle-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-cinoton-bundle-panel]'));
      this.selects = Array.from(this.querySelectorAll('[data-cinoton-bundle-variant]'));
      this.total = this.querySelector('[data-cinoton-bundle-total]');
      this.button = this.querySelector('[data-cinoton-bundle-add]');
      this.message = this.querySelector('[data-cinoton-bundle-message]');
      this.quantity = document.querySelector('.cinoton-product-detail .quantity__input');

      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectBundle(tab.dataset.cinotonBundleTab)));
      this.selects.forEach((select) => select.addEventListener('change', () => this.updateTotal()));
      document.addEventListener('input', (event) => {
        if (event.target.matches('.cinoton-product-detail .quantity__input')) this.updateTotal();
      });
      this.button?.addEventListener('click', () => this.addBundle());
      document.addEventListener('product:variant-change', (event) => this.syncCurrentVariant(event.detail?.variant));
      this.updateTotal();
    }

    selectBundle(index) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.cinotonBundleTab === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.cinotonBundlePanel === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      this.updateTotal();
    }

    syncCurrentVariant(variant) {
      if (!variant) return;
      const current = this.querySelector('[data-cinoton-bundle-current]');
      if (!current) return;
      const matching = Array.from(current.options).find((option) => Number(option.value) === Number(variant.id));
      if (matching) {
        current.value = matching.value;
        this.updateTotal();
      }
    }

    activeSelects() {
      const current = this.querySelector('[data-cinoton-bundle-current]');
      const activePanel = this.querySelector('[data-cinoton-bundle-panel]:not([hidden])');
      const addon = activePanel?.querySelector('[data-cinoton-bundle-variant]');
      return [current, addon].filter(Boolean);
    }

    money(cents) {
      return formatMoney(cents, this.dataset.moneyFormat);
    }

    updateTotal() {
      const quantity = Math.max(1, Number(document.querySelector('.cinoton-product-detail .quantity__input')?.value || 1));
      const selects = this.activeSelects();
      const cents = selects.reduce((sum, select, index) => {
        const price = Number(select.selectedOptions[0]?.dataset.price || 0);
        return sum + price * (index === 0 ? quantity : 1);
      }, 0);
      if (this.total) this.total.innerHTML = this.money(cents);
      selects.forEach((select) => {
        const price = Number(select.selectedOptions[0]?.dataset.price || 0);
        const priceNode = select.closest('.cinoton-product-bundle__info')?.querySelector('.cinoton-product-bundle__price');
        if (priceNode) priceNode.innerHTML = this.money(price);
      });
    }

    async addBundle() {
      if (!this.button || this.button.disabled) return;
      const quantity = Math.max(1, Number(document.querySelector('.cinoton-product-detail .quantity__input')?.value || 1));
      const items = this.activeSelects().map((select, index) => ({
        id: Number(select.value),
        quantity: index === 0 ? quantity : 1
      }));
      if (!items.length) return;

      this.button.disabled = true;
      this.button.classList.add('loading');
      if (this.message) this.message.textContent = '';
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this bundle.');
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: result.items?.[0] || result } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        if (this.message) this.message.textContent = 'Bundle added to cart.';
      } catch (error) {
        if (this.message) this.message.textContent = error.message;
      } finally {
        this.button.disabled = false;
        this.button.classList.remove('loading');
      }
    }
  }

  if (!customElements.get('cinoton-product-bundle')) {
    customElements.define('cinoton-product-bundle', CinotonProductBundle);
  }

  const onReady = () => {
    const supportLinks = Array.from(document.querySelectorAll('[data-cinoton-summary-target]'));
    const supportTarget = (link) => [document.querySelector(link.getAttribute('href')), document.querySelector(link.dataset.cinotonNavFallback || '[data-missing-support-target]')].find((target) => target && target.getClientRects().length);
    const updateSupportLinks = () => supportLinks.forEach((link) => {
      link.hidden = !supportTarget(link);
    });
    if (supportLinks.length) {
      updateSupportLinks();
      new MutationObserver(updateSupportLinks).observe(document.body, {childList: true, subtree: true});
      supportLinks.forEach((link) => link.addEventListener('click', (event) => {
        const target = supportTarget(link);
        if (target) { event.preventDefault(); target.scrollIntoView({behavior: 'smooth'}); }
      }));
    }
    document.querySelectorAll('[data-cinoton-product-detail]').forEach((product) => {
      const thumbnails = Array.from(product.querySelectorAll('.product-thumbnail'));
      const modeButtons = product.querySelectorAll('[data-cinoton-media-target]');

      const syncMediaMode = (thumbnail) => {
        if (!thumbnail) return;
        const isVideo = thumbnail.dataset.mediaType === 'video' || thumbnail.dataset.mediaType === 'external_video';
        modeButtons.forEach((button) => {
          const active = button.dataset.cinotonMediaTarget === 'overview' ? !isVideo : isVideo;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      };

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => requestAnimationFrame(() => syncMediaMode(thumbnail)));
      });

      const selectedThumbnail = thumbnails.find((thumbnail) => thumbnail.classList.contains('is-initial-selected')) || thumbnails[0];
      syncMediaMode(selectedThumbnail);

      if (thumbnails.length > 1) {
        const selectedMediaObserver = new MutationObserver(() => {
          const selected = thumbnails.find((thumbnail) => thumbnail.classList.contains('is-initial-selected'));
          if (selected) syncMediaMode(selected);
        });
        thumbnails.forEach((thumbnail) => selectedMediaObserver.observe(thumbnail, { attributes: true, attributeFilter: ['class'] }));
      }

      modeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const target = button.dataset.cinotonMediaTarget;
          const thumbnail = target === 'overview'
            ? thumbnails.find((item) => item.dataset.mediaType === 'image')
            : thumbnails.find((item) => item.dataset.mediaId === target);

          if (thumbnail) thumbnail.click();
          syncMediaMode(thumbnail);
        });
      });
    });

    document.querySelectorAll('product-add-to-cart-sticky').forEach((sticky) => {
      const buyNow = sticky.querySelector('[data-cinoton-buy-now]');

      if (buyNow) {
        buyNow.addEventListener('click', () => {
          const section = sticky.dataset.section;
          const form = document.getElementById(`product-form-${section}`);
          const dynamicCheckout = form?.querySelector('.shopify-payment-button__button');
          const addButton = form?.querySelector('[name="add"]');

          if (dynamicCheckout && !dynamicCheckout.disabled) {
            dynamicCheckout.click();
          } else if (form && addButton && !addButton.disabled) {
            form.requestSubmit(addButton);
          }
        });
      }
    });

    document.querySelectorAll('[data-cinoton-product-summary]').forEach((summary) => {
      const toggle = summary.querySelector('[data-cinoton-summary-toggle]');
      const text = summary.querySelector('[data-cinoton-summary-text]');
      const toggleLabel = toggle?.querySelector('[data-cinoton-summary-toggle-label]');
      if (toggle && text) {
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          if (toggleLabel) toggleLabel.textContent = expanded ? (toggleLabel.dataset.moreLabel || 'Show More') : (toggleLabel.dataset.lessLabel || 'Show Less');
          text.classList.toggle('is-collapsed', expanded);
        });
      }

      const shippingOpen = summary.querySelector('[data-cinoton-shipping-open]');
      const shippingModal = summary.querySelector('[data-cinoton-shipping-modal]');
      const shippingDialog = shippingModal?.querySelector('.cinoton-shipping-modal__dialog');
      const shippingClose = shippingModal?.querySelector('.cinoton-shipping-modal__close');
      if (!shippingOpen || !shippingModal || !shippingDialog || !shippingClose) return;

      const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const setShippingModal = (open) => {
        shippingOpen.setAttribute('aria-expanded', String(open));
        shippingModal.hidden = !open;
        document.body.classList.toggle('cinoton-pdp-modal-open', open);
        if (open) {
          window.requestAnimationFrame(() => shippingClose.focus());
        } else {
          shippingOpen.focus();
        }
      };

      shippingOpen.addEventListener('click', () => setShippingModal(true));
      shippingModal.querySelectorAll('[data-cinoton-shipping-close]').forEach((close) => {
        close.addEventListener('click', () => setShippingModal(false));
      });
      shippingModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setShippingModal(false);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(shippingDialog.querySelectorAll(focusableSelector));
        if (!focusable.length) {
          event.preventDefault();
          shippingDialog.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });

    document.querySelectorAll('[data-cinoton-product-promotion]').forEach((promotion) => {
      const toggle = promotion.querySelector('[data-cinoton-promotion-toggle]');
      const panel = promotion.querySelector('[data-cinoton-promotion-panel]');
      const price = promotion.querySelector('[data-cinoton-trend-price]');
      if (!toggle || !panel) return;

      const setExpanded = (expanded) => {
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
        promotion.classList.toggle('is-trend-open', expanded);
      };

      toggle.addEventListener('click', () => setExpanded(toggle.getAttribute('aria-expanded') !== 'true'));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          setExpanded(false);
          toggle.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!promotion.contains(event.target) && toggle.getAttribute('aria-expanded') === 'true') setExpanded(false);
      });
      document.addEventListener('product:variant-change', (event) => {
        if (event.detail?.sectionId && promotion.dataset.section !== event.detail.sectionId) return;
        if (price && event.detail?.variant) price.innerHTML = formatMoney(Number(event.detail.variant.price || 0), promotion.dataset.moneyFormat);
      });
    });

    document.addEventListener('product:variant-change', (event) => {
      document.querySelectorAll('[data-cinoton-variant-saving]').forEach((saving) => {
        if (event.detail?.sectionId && saving.closest('[data-section]')?.dataset.section !== event.detail.sectionId) return;
        const variant = event.detail?.variant;
        const percent = variant && variant.compare_at_price > variant.price ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
        saving.textContent = `Save ${percent}%`;
        saving.hidden = percent <= 0;
      });
      const sku = document.querySelector('[data-cinoton-summary-sku]');
      if (sku && event.detail?.variant) sku.textContent = event.detail.variant.sku || '';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
})();
